import SectionContainer from './SectionContainer'
import classNames from 'classnames'
import { APattern } from 'components/Svgs/APattern.js'
import { MediaImageStandard } from 'components/Contentful'
import { rewriteAssetUrl } from 'utils/conversions'

const bgBlue = 'blue'
const bgDefault = 'bgDefault'
const bgPrimaryHighlightBlue = 'bgPrimaryHighlightBlue'
const bgShadowPrimaryHighlightBlue = 'bgShadowPrimaryHighlightBlue'
const bgShadowPrimaryHighlightWhiteBlue = 'bgShadowPrimaryHighlightWhiteBlue'
const blueGradient1 = 'blueGradient1'
const neutralGrey8 = 'neutralGrey8'
const purpleBlueGradient1 = 'purpleBlueGradient1'
const white = 'white'
const achieveHighlightBlue = 'achieveHighlightBlue'
const bgBlue100 = 'blue100'
const bgBlue25 = 'blue25'

export const SECTION_VARIANTS = {
  bgBlue,
  bgPrimaryHighlightBlue,
  bgDefault,
  bgShadowPrimaryHighlightBlue,
  bgShadowPrimaryHighlightWhiteBlue,
  blueGradient1,
  purpleBlueGradient1,
  neutralGrey8,
  white,
  achieveHighlightBlue,
  bgBlue100,
  bgBlue25,
}
function formatMediaUrl(mediaContentURL) {
  return mediaContentURL && !mediaContentURL.includes('.svg')
    ? `${mediaContentURL}?fm=webp&q=50`
    : mediaContentURL
}
function Section({
  children,
  defaultPadY = false,
  backgroundColor,
  backgroundImageAlt = '',
  mediaContent,
  middleMediaContent,
  mobileMediaContent,
  backgroundObjectPositionClass: backgroundObjectPositionClassProp,
  backgroundObjectPositionclassName,
  backgroundObjectPosition = 'top',
  backgroundObjectPositionMobile,
  childContainerClassName,
  className,
  id,
  sectionStyles,
  showPattern = false,
  contain = true,
  variant = bgDefault,
  preloadBackground = false,
  backgroundNoCss = false,
  full = false,
  relative = true,
  fullBackground = false,
  disableGutters = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  maxWidth,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  mobileVariant,
  ...restProps
}) {
  const backgroundObjectPositionClass =
    backgroundObjectPositionClassProp ?? backgroundObjectPositionclassName
  const hasBg = Boolean(mediaContent || middleMediaContent || mobileMediaContent)

  // This handles images from both GraphQA (flat) or rest api structure
  let mobileMediaUrl = mobileMediaContent?.url ?? mobileMediaContent?.fields?.file?.url ?? null
  let middleMediaUrl = middleMediaContent?.url ?? middleMediaContent?.fields?.file?.url ?? null
  let mediaUrl = mediaContent?.url ?? mediaContent?.fields?.file?.url ?? null

  mobileMediaUrl = formatMediaUrl(mobileMediaUrl)
  middleMediaUrl = formatMediaUrl(middleMediaUrl)
  mediaUrl = formatMediaUrl(mediaUrl)

  const contents = contain ? (
    <SectionContainer className={childContainerClassName} disableGutters={disableGutters}>
      {children}
    </SectionContainer>
  ) : (
    children
  )

  const fullBackgroundMap = {
    neutralGrey8: 'bg-neutral-25',
    achieveHighlightBlue: 'bg-[#eff5ff]',
    blue25: 'bg-[#f5f9ff]',
    blue: 'bg-[#3300ff]',
    blue100: 'bg-[#d2e2fe]',
    bgPrimaryHighlightBlue: 'bg-[#eff5ff]',
    white: 'bg-white',
    bgPrimaryHighlightBlue2: 'bg-[#f5f9ff]',
    bgShadowPrimaryHighlightBlue: 'bgShadowPrimaryHighlightBlue',
    bgShadowPrimaryHighlightWhiteBlue: 'bgShadowPrimaryHighlightWhiteBlue',
  }

  return (
    <div className="relative">
      {fullBackground && (
        <div
          className={`absolute top-0 left-1/2 -translate-x-1/2 w-full h-full ${
            fullBackgroundMap[variant ?? bgDefault]
          } -z-10`}
        />
      )}
      <section
        id={id}
        style={{
          // TODO: lets remove explicit backgroundColor and just allow a style object to be passed
          // leaving the responsibility of custom css to be accurately verified by CMS user.
          backgroundColor,
          ...sectionStyles,
        }}
        className={classNames(
          relative &&
            'relative data-[default-pad-y="true"]:py-12 lg:data-[default-pad-y="true"]:py-16',
          'data-[variant="blueGradient1"]:blueGradient1',
          'data-[variant="purpleBlueGradient1"]:purpleBlueGradient1',
          'data-[variant="neutralGrey8"]:bg-neutral-25',
          'data-[variant="achieveHighlightBlue"]:bg-[#eff5ff]',
          'data-[variant="blue25"]:bg-[#f5f9ff]',
          'data-[variant="blue"]:bg-[#3300ff]',
          'data-[variant="blue100"]:bg-[#d2e2fe]',
          'data-[variant="bgPrimaryHighlightBlue"]:bg-[#eff5ff]',
          'data-[variant="white"]:bg-white',
          'data-[variant="bgPrimaryHighlightBlue2"]:bg-[#f5f9ff]',
          'data-[variant="bgShadowPrimaryHighlightBlue"]:bgShadowPrimaryHighlightBlue',
          'data-[variant="bgShadowPrimaryHighlightWhiteBlue"]:bgShadowPrimaryHighlightWhiteBlue',
          className
        )}
        data-variant={variant}
        data-default-pad-y={defaultPadY}
        {...restProps}
      >
        {showPattern && (
          <span
            className={
              'box-border block overflow-hidden w-[initial] h-[initial] opacity-100 absolute m-0 p-0 border-0 inset-0 bg-transparent'
            }
          >
            <APattern title="Achieve pattern" className={'opacity-5 scale-125'} />
          </span>
        )}
        {/* Only display the a full background image when present and background pattern is disabled */}
        {!showPattern && hasBg && !backgroundNoCss && (
          <>
            <div
              role="img"
              aria-label={backgroundImageAlt}
              className={classNames(
                'absolute bg-cover inset-0',
                `${!full ? 'md:hidden block h-[345px] ' : 'lg:hidden block'}`,
                backgroundObjectPositionClass
              )}
              style={{
                backgroundImage: `url(${rewriteAssetUrl(mobileMediaUrl)})`,
              }}
            />
            <div
              role="img"
              aria-label={backgroundImageAlt}
              className={classNames(
                'hidden absolute bg-cover inset-0',
                `${!full ? 'md:block md:h-[345px] lg:hidden' : 'md:block lg:hidden'}`,
                backgroundObjectPositionClass
              )}
              style={{
                backgroundImage: `url(${rewriteAssetUrl(middleMediaUrl)})`,
              }}
            />
            <div
              role="img"
              aria-label={backgroundImageAlt}
              className={classNames(
                'hidden absolute bg-cover inset-0',
                'md:hidden lg:block',
                backgroundObjectPositionClass
              )}
              fetchPriority="high"
              style={{
                backgroundImage: `url(${rewriteAssetUrl(mediaUrl)})`,
              }}
            />
          </>
        )}

        {!showPattern && hasBg && backgroundNoCss && (
          <>
            <div
              aria-label={backgroundImageAlt}
              className={classNames(
                'hidden absolute bg-cover inset-0',
                'block! h-[345px]',
                'md:hidden',
                'lg:hidden'
              )}
            >
              <MediaImageStandard
                content={mobileMediaContent}
                layout="fill"
                objectFit="cover"
                alt={backgroundImageAlt}
                objectPosition={backgroundObjectPosition}
                objectPositionMobile={backgroundObjectPositionMobile}
                // backgroundObjectPositionclassName={backgroundObjectPositionClass}
                fetchPriority="high"
                preload={preloadBackground}
              />
            </div>
            <div
              aria-label={backgroundImageAlt}
              className={classNames(
                'hidden absolute bg-cover inset-0',
                'md:block! md:h-[345px]',
                'lg:hidden!'
              )}
            >
              <MediaImageStandard
                content={middleMediaContent}
                layout="fill"
                objectFit="cover"
                alt={backgroundImageAlt}
                objectPosition={backgroundObjectPosition}
                objectPositionMobile={backgroundObjectPositionMobile}
                // backgroundObjectPositionclassName={backgroundObjectPositionClass}
                fetchPriority="high"
                preload={preloadBackground}
              />
            </div>
            <div
              aria-label={backgroundImageAlt}
              className={classNames('hidden absolute bg-cover inset-0', 'md:hidden! lg:block!')}
            >
              <MediaImageStandard
                content={mediaContent}
                layout="fill"
                objectFit="cover"
                alt={backgroundImageAlt}
                objectPosition={backgroundObjectPosition}
                objectPositionMobile={backgroundObjectPositionMobile}
                // backgroundObjectPositionclassName={backgroundObjectPositionClass}
                fetchPriority="high"
                preload={preloadBackground}
              />
            </div>
          </>
        )}
        {contents}
      </section>
    </div>
  )
}

export default Section
